import { batch } from 'react-redux';
import { priceImprovementInitialized } from 'state/quotes/priceEnhancements/priceImprovementRequest/priceImprovementRequest.actions';
import { getIsTradeEnabled } from 'state/selectors';
import type { Thunk } from 'state/state.common';
import { tradeConfirmationInitBackToBack } from 'state/uiPanelState/tradeConfirmation/tradeConfirm.action';
import type { BackToBackType } from 'state/uiPanelState/tradeConfirmation/tradeConfirm.model';

import {
  addonFormResetRequested,
  addonQuoteReceived,
  addonQuoteRemoved,
  addonQuoteRequested,
  addonQuoteTradeRequested,
  addonTabCreated,
} from './addonForm.action';
import type { AddonQuoteResult } from './addonForm.model';
import * as addonSelector from './addonForm.selector';

type AddonTradeConfirmationBackToBack = {
  isBackToBack: boolean;
  backToBackType?: BackToBackType;
  fileCode?: string;
  noteNominal?: number;
  isDeltaAdjusted?: boolean;
  isDelayedBackToBack?: boolean;
  backedNotional?: number;
};

function getAddonBackToBackInitialization(
  state: Parameters<Thunk<void>>[1] extends () => infer T ? T : never,
  tabId: string,
  quote: AddonQuoteResult,
): AddonTradeConfirmationBackToBack {
  return {
    isBackToBack: addonSelector.isBackToBack(state, tabId) ?? false,
    backToBackType: addonSelector.getBackToBackType(state, tabId) as BackToBackType | undefined,
    fileCode: addonSelector.getBackToBackFileCode(state, tabId),
    noteNominal: addonSelector.getBackToBackNoteNominal(state, tabId),
    isDeltaAdjusted: addonSelector.getBackToBackIsDeltaAdjusted(state, tabId),
    isDelayedBackToBack: addonSelector.isDelayedBackToBack(state, tabId),
    backedNotional: quote.backedNotional,
  };
}

export function addonTabCreatedThunk(productId: string, rfqId: string, tabName: string): Thunk<void> {
  return (dispatch, _getState, { getNewGuid }) => {
    const tabId = getNewGuid();
    dispatch(addonTabCreated(tabId, productId, rfqId, tabName));
  };
}

export function addonQuoteRequestedThunk(tabId: string): Thunk<void> {
  return (dispatch, getState, { getNewGuid, getNow }) => {
    const state = getState();
    const quoteId = addonSelector.getLastQuoteId(state, tabId)!;
    batch(() => {
      if (quoteId) {
        dispatch(addonQuoteRemoved(tabId, quoteId));
      }
      const newQuoteId = getNewGuid();

      dispatch(addonQuoteRequested(tabId, newQuoteId, getNow().getTime()));
    });
  };
}

export function addonFormResetThunk(tabId: string): Thunk<void> {
  return (dispatch, getState) => {
    const state = getState();
    const quoteId = addonSelector.getLastQuoteId(state, tabId)!;
    const originalQuoteId = addonSelector.getOriginalQuoteId(state, tabId)!;
    const productId = addonSelector.getProductId(state, tabId)!;

    batch(() => {
      if (quoteId) {
        dispatch(addonQuoteRemoved(tabId, quoteId));
      }

      dispatch(addonFormResetRequested(tabId, productId, originalQuoteId));
    });
  };
}

export function addonQuoteTradeRequestedThunk(tabId: string): Thunk<void> {
  return (dispatch, getState) => {
    const state = getState();
    const quoteId = addonSelector.getLastQuoteId(state, tabId)!;
    dispatch(addonQuoteTradeRequested(tabId, quoteId));
  };
}

export function addonQuoteReceivedThunk(
  tabId: string,
  quoteId: string,
  quote: AddonQuoteResult,
  timeToTradeInMs: number,
): Thunk<void> {
  return (dispatch, getState) => {
    const state = getState();
    const {
      isBackToBack,
      backToBackType,
      fileCode,
      noteNominal,
      isDeltaAdjusted,
      isDelayedBackToBack,
      backedNotional,
    } = getAddonBackToBackInitialization(state, tabId, quote);
    const hasTradableRights = getIsTradeEnabled(state);

    batch(() => {
      dispatch(addonQuoteReceived(tabId, quoteId, quote, timeToTradeInMs));
      if (isBackToBack) {
        dispatch(
          tradeConfirmationInitBackToBack(
            quoteId,
            true,
            fileCode,
            noteNominal,
            isDeltaAdjusted,
            backToBackType,
            isDelayedBackToBack,
            backedNotional,
          ),
        );
      }
      if (hasTradableRights) {
        dispatch(priceImprovementInitialized(quoteId));
      }
    });
  };
}
